import React, { useEffect } from "react";
import { Download, X, Paperclip } from "lucide-react";

// Lightbox-style modal that previews an attachment inline when possible
// (image, pdf, video, audio) and falls back to a Download button when not.
//
// File type is inferred from `mime` if present, otherwise the filename
// extension. The header always offers a Download link so users can grab
// the original even when the preview renders.
//
// `url` should already be the authenticated download URL (cookie carries
// over because the same-origin <img>/<iframe> request includes credentials
// by default for same-site / configured CORS — works in our setup since
// the boards-service has `Access-Control-Allow-Credentials`).

function extOf(filename) {
  if (!filename) return "";
  const i = filename.lastIndexOf(".");
  return i >= 0 ? filename.slice(i + 1).toLowerCase() : "";
}

function kindOf(filename, mime) {
  const m = (mime || "").toLowerCase();
  const ext = extOf(filename);
  if (m.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(ext)) return "image";
  if (m === "application/pdf" || ext === "pdf") return "pdf";
  if (m.startsWith("video/") || ["mp4", "webm", "mov", "m4v"].includes(ext)) return "video";
  if (m.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a"].includes(ext)) return "audio";
  return "generic";
}

export default function AttachmentPreview({ url, filename, mime, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!url) return null;
  const kind = kindOf(filename, mime);
  const displayName = filename || "Attachment";

  return (
    <div className="att-preview-backdrop" onClick={onClose}>
      <div
        className={`att-preview kind-${kind}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Preview ${displayName}`}
      >
        <header className="att-preview-head">
          <span className="att-preview-name" title={displayName}>{displayName}</span>
          <div className="att-preview-actions">
            <a
              className="att-preview-action"
              href={url}
              download={filename || ""}
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={14} className="me-1" /> Download
            </a>
            <a
              className="att-preview-action"
              href={url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              ↗ Open
            </a>
            <button className="att-preview-close" onClick={onClose} title="Close (Esc)" aria-label="Close"><X size={14} /></button>
          </div>
        </header>

        <div className="att-preview-body">
          {kind === "image" && (
            <img src={url} alt={displayName} className="att-preview-img" />
          )}
          {kind === "pdf" && (
            <iframe src={url} title={displayName} className="att-preview-frame" />
          )}
          {kind === "video" && (
            <video src={url} controls className="att-preview-video" />
          )}
          {kind === "audio" && (
            <audio src={url} controls className="att-preview-audio" />
          )}
          {kind === "generic" && (
            <div className="att-preview-fallback">
              <div className="att-preview-icon"><Paperclip size={28} /></div>
              <p>No inline preview for this file type.</p>
              <a className="att-preview-cta" href={url} download={filename || ""}>
                <span className="d-inline-flex align-items-center gap-1"><Download size={14} /> Download {displayName}</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
